import { useEffect, useState } from "react";
import { Flag, CheckCircle2, Loader2, Wand2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import QuestionEditor from "@/components/admin/QuestionEditor";

const REASON = { missing: "Picture missing", wrong: "Wrong picture", shows_answer: "Shows the answer", other: "Other" };
const FIX = { none: "No picture to redraw", redrawing: "Redrawing…", redrawn: "Picture redrawn ✓", hidden: "Redraw failed — needs you",
  failed: "Redraw failed — needs you", skipped: "AI busy — try later", already_redrawn_recently: "Redrawn recently", ok: "Picture checked ✓",
  pending: "New picture — waiting for your OK", admin_picture: "Your own picture — kept, please check" };

export default function QuestionReports() {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/question-reports").then(({ data }) => setData(data)).catch(() => setData({ reports: [], open: 0 }));
  useEffect(() => { load(); }, []);
  const resolve = async (id) => {
    try {
      const { data } = await api.post(`/admin/question-reports/${id}/resolve`);
      toast.success(data.thanked ? "Marked as fixed — thank-you email sent to the parent" : "Marked as fixed");
      load();
    }
    catch { toast.error("Could not update the report"); }
  };
  if (!data) return <Loader2 className="animate-spin mx-auto" />;
  const rows = showAll ? data.reports : data.reports.filter((r) => r.status === "open");

  return (
    <div data-testid="admin-reports">
      <QuestionEditor target={editing} onClose={() => setEditing(null)} />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="bg-slate-800 rounded-2xl border-2 border-rose-400/60 px-4 py-2 flex items-center gap-2" data-testid="reports-open-count">
          <Flag size={18} className="text-rose-300" /> <span className="font-bold">{data.open}</span> <span className="text-slate-300 text-sm">open report(s)</span>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="w-4 h-4 accent-yellow-300" data-testid="reports-show-all" /> Show fixed reports too
        </label>
        <p className="text-xs text-slate-400 w-full">Tip: check or redraw the picture in the Pictures tab, then click "Mark as fixed".</p>
      </div>
      {!rows.length ? <p className="text-slate-400 text-center py-8" data-testid="reports-empty">No reports. 🎉</p> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="bg-slate-800 rounded-2xl border-2 border-slate-700 p-3 text-sm" data-testid={`report-row-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-rose-500/20 text-rose-200 px-2 py-0.5 text-xs font-bold">{REASON[r.reason]}</span>
                <span className="font-semibold">{r.subject} · G{r.grade} · {r.chapter_title} · level {r.level} · Q{r.qi + 1}</span>
                <span className="text-slate-400 text-xs">{new Date(r.created_at).toLocaleString("en-GB")} · {r.source === "demo" ? "Demo" : "Quiz"}</span>
                <span className="ml-auto inline-flex items-center gap-1 text-xs text-sky-200" data-testid={`report-autofix-${r.id}`}><Wand2 size={13} /> {FIX[r.auto_fix] || r.auto_fix}</span>
              </div>
              <p className="mt-1.5">{r.question}</p>
              {r.note && <p className="text-slate-300 mt-1">Note: “{r.note}”</p>}
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="text-xs text-slate-400">{r.parent_name} · {r.parent_email}</span>
                <button onClick={() => setEditing({ chapter_id: r.chapter_id, level: r.level, qi: r.qi })} data-testid={`report-edit-${r.id}`}
                  className="ml-auto inline-flex items-center gap-1 rounded-full bg-blue-500 text-white px-3 py-1 text-xs font-bold"><Pencil size={13} /> Edit question</button>
                {r.status === "open" ? (
                  <button onClick={() => resolve(r.id)} data-testid={`report-resolve-${r.id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-400 text-slate-900 px-3 py-1 text-xs font-bold"><CheckCircle2 size={13} /> Mark as fixed</button>
                ) : <span className="text-xs text-emerald-300">Fixed {r.resolved_at ? new Date(r.resolved_at).toLocaleDateString("en-GB") : ""}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
